import { useEffect, useMemo, useState } from "react";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatMoney, sumDecimals } from "@/lib/format";
import EntryDialog from "@/components/directors/EntryDialog";
import EntriesGrid, { groupLines } from "@/components/directors/EntriesGrid";

const FilterSelect = ({ value, onChange, items, all, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-44" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Pager = ({ page, pages, setPage, bottom }) => (
  <div className="ml-auto flex items-center gap-2" data-testid={bottom ? "entries-pager-bottom" : "entries-pager"}>
    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`entries-prev${bottom ? "-bottom" : ""}`}><ChevronLeft className="h-4 w-4" /></Button>
    <span className="text-sm font-mono" data-testid={`entries-page${bottom ? "-bottom" : ""}`}>Page {page + 1} of {pages}</span>
    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`entries-next${bottom ? "-bottom" : ""}`}><ChevronRight className="h-4 w-4" /></Button>
  </div>
);

const PAGE = 100;
const runningFy = () => {
  const d = new Date();
  const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(2)}`;
};

export default function EntriesTab({ directors }) {
  const api = useApi();
  const { data: entries, reload } = useResource("/director_entries");
  const [dir, setDir] = useState("all");
  const [fy, setFy] = useState(null);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const fys = [...new Set((entries || []).map((e) => e.fy))].sort();
  // default to the running financial year (or the latest one with entries)
  useEffect(() => {
    if (entries && fy === null) setFy(fys.includes(runningFy()) ? runningFy() : fys[fys.length - 1] || "all");
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [fy, dir, q]);

  const visible = useMemo(() => (entries || []).filter((e) =>
    (fy === "all" || e.fy === fy) && (!q.trim() || e.description.toLowerCase().includes(q.trim().toLowerCase())))
    .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id)), [entries, fy, q]);
  const lines = useMemo(() => groupLines(visible).filter((l) => dir === "all" || l.entries.some((e) => e.director === dir)), [visible, dir]);
  const shown = dir === "all" ? directors : directors.filter((d) => d.id === dir);
  const pages = Math.max(1, Math.ceil(lines.length / PAGE));
  const pageLines = lines.slice(page * PAGE, page * PAGE + PAGE);

  const save = async (f) => {
    try {
      if (f.id) await api("put", `/director_entries/${f.id}`, { date: f.date, description: f.description, director: f.director, debit: f.debit, credit: f.credit });
      else if (f.split) await api("post", "/director_entries/split", { date: f.date, description: f.description, directors: f.directors, debit: f.debit, credit: f.credit, mode: f.mode });
      else await api("post", "/director_entries", { date: f.date, description: f.description, director: f.director, debit: f.debit, credit: f.credit });
      toast.success("Entry saved");
      setEditing(null);
      reload();
    } catch (e) { return errorText(e); }
  };
  const removeLine = async (l) => {
    try {
      for (const e of l.entries) await api("delete", `/director_entries/${e.id}`);
      toast.success(l.entries.length > 1 ? `Deleted ${l.entries.length} entries` : "Entry deleted");
    } catch (x) { toast.error(errorText(x)); }
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description…" className="pl-9 w-56" data-testid="entries-search-input" />
        </div>
        <FilterSelect value={dir} onChange={setDir} all="All directors" testId="entries-director-filter" items={directors.map((d) => ({ value: d.id, label: d.name }))} />
        <FilterSelect value={fy || "all"} onChange={setFy} all="All years" testId="entries-fy-filter" items={fys.map((f) => ({ value: f, label: f }))} />
        <Button className="ml-auto" onClick={() => setEditing({})} data-testid="add-entry-button"><Plus className="h-4 w-4 mr-1" /> Add entry</Button>
      </div>
      <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
        {directors.map((d) => {
          const rows = visible.filter((e) => e.director === d.id);
          return (
            <div key={d.id} className="border border-border bg-card p-4" data-testid={`entries-totals-${d.name}`}>
              <div className="font-display font-semibold">{d.name}</div>
              <div className="mt-2 grid grid-cols-2 text-xs font-mono">
                <span className="text-muted-foreground">Debits</span><span className="text-right text-rose-300">{formatMoney(sumDecimals(rows.map((r) => r.debit)))}</span>
                <span className="text-muted-foreground">Credits</span><span className="text-right text-emerald-300">{formatMoney(sumDecimals(rows.map((r) => r.credit)))}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-muted-foreground" data-testid="entries-count">
          {lines.length ? `${lines.length} ${lines.length === 1 ? "line" : "lines"}${fy && fy !== "all" ? ` in ${fy}` : ""}, newest first. Showing ${page * PAGE + 1}–${Math.min(lines.length, (page + 1) * PAGE)}. Click an amount to edit it.` : "No entries match"}
        </div>
        {pages > 1 && <Pager page={page} pages={pages} setPage={setPage} />}
      </div>
      <EntriesGrid lines={pageLines} totalLines={lines} directors={shown} onEdit={setEditing} onDelete={removeLine} />
      {pages > 1 && <div className="flex justify-end"><Pager page={page} pages={pages} setPage={setPage} bottom /></div>}
      <EntryDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initial={editing} directors={directors} onSubmit={save} />
    </div>
  );
}
